import React from 'react';
import moment from 'moment';

import SVGIcon from 'src/components/svg';
import MediaFiles from './MediaFiles';
import { urlRegx } from 'src/helpers';
import { MCButton, MCText } from 'components/basic';
import LANG_CONST from 'src/helpers/lang/const';
import AlertManager from 'components/alert/AlertManager';

// A text is shown in full if it has <= MAX_CHARS chars AND <= MAX_LINES lines.
const MAX_LINES = 10;
const MAX_CHARS = 600;

// If the text is longer, only a short preview is shown (smaller than the limits above).
// This avoids the case: 601 chars -> cut to 595 chars -> "View more" is not worth it.
const PREVIEW_LINES = 5;
const PREVIEW_CHARS = 300;

// If the preview is one single long word / URL at the very beginning,
// keep it whole when it is not longer than this. Longer than this: hard cut.
const MAX_KEEP_TOKEN_CHARS = 300;

// Duration (ms) of the expand / collapse animation
const TOGGLE_ANIMATION_MS = 250;
// Text up to these limits is expanded inside the chat bubble.
// Longer text should open the reader popup instead.
const EXPAND_MAX_CHARS = 900;
const EXPAND_MAX_LINES = 15;

const isSpace = (ch) => /\s/.test(ch);

// Shared helper: turn URLs in a text into <a> links.
export const renderTextWithLinks = (text) => {
  const str = text == null ? '' : text.toString();
  if (!str) return str;

  const urls = str.match(urlRegx);
  if (!urls || !urls.length) return str;

  const elements = [];
  let count = 0; // running offset: where the last link ended

  urls.forEach((url, i) => {
    const index = str.indexOf(url, count); // search from `count`, not from 0
    if (index === -1) return;

    if (index > count) elements.push(str.substring(count, index));
    elements.push(
      <a
        key={`link-${i}-${index}`}
        href={url.indexOf('http') !== 0 ? 'http://' + url : url}
        rel='noreferrer'
        target='_blank'
        className='mc-pointer mc-color-primary'
        style={{ wordBreak: 'break-all' }}
      >
        {url}
      </a>
    );
    count = index + url.length;
  });

  // Always add the remaining text, even if the last URL was skipped
  if (count < str.length) elements.push(str.substring(count));

  return elements.length ? elements : str;
};

// Returns { text, truncated }
export const getTruncatedText = (text) => {
  const str = text == null ? '' : text.toString();

  // 1. Short text: show everything
  let lineCount = 1;
  for (let i = 0; i < str.length; i++) {
    if (str[i] === '\n') lineCount++;
  }
  if (str.length <= MAX_CHARS && lineCount <= MAX_LINES) {
    return { text: str, truncated: false };
  }

  // 2. Long text: find the cut position (PREVIEW_CHARS or PREVIEW_LINES, whichever comes first)
  let cut = Math.min(str.length, PREVIEW_CHARS);

  let pos = -1;
  for (let i = 0; i < PREVIEW_LINES; i++) {
    pos = str.indexOf('\n', pos + 1);
    if (pos === -1) break;
  }
  if (pos !== -1) cut = Math.min(cut, pos); // pos = the line break after the last kept line

  // 3. Move the cut to a word boundary (so words, URLs and emojis are not cut in the middle).
  // A URL has no space inside, so this rule also protects URLs.
  if (cut < str.length && !isSpace(str[cut])) {
    // Find the start of the word/URL that contains the cut position
    let tokenStart = cut;
    while (tokenStart > 0 && !isSpace(str[tokenStart - 1])) tokenStart--;

    if (str.slice(0, tokenStart).trim()) {
      // There is text before this word: cut before the word
      cut = tokenStart;
    } else {
      // The preview is just one big word/URL
      let tokenEnd = cut;
      while (tokenEnd < str.length && !isSpace(str[tokenEnd])) tokenEnd++;

      if (tokenEnd <= MAX_KEEP_TOKEN_CHARS) {
        cut = tokenEnd; // not too long: keep it whole
      } else {
        // Very long (long URL, or text without spaces like Chinese): hard cut.
        // Just make sure we do not split an emoji (surrogate pair).
        const code = str.charCodeAt(cut - 1);
        if (code >= 0xd800 && code <= 0xdbff) cut -= 1;
      }
    }
  }

  return { text: str.slice(0, cut).trimEnd(), truncated: true };
};

export default class TextItem extends React.PureComponent {
  state = { showMore: false };

  // Ref to the <pre> element, used to measure height and run the animation
  preRef = React.createRef();
  // Height of the text before toggle (animation starts from this value)
  prevHeight = 0;

  componentDidUpdate(prevProps, prevState) {
    // Only animate when the user toggles View more / View less
    if (prevState.showMore === this.state.showMore) return;

    const el = this.preRef.current;
    if (!el || !el.animate) return; // old browser: no animation, just switch

    const from = this.prevHeight;
    const to = el.offsetHeight; // height of the new text
    if (!from || from === to) return;

    // Animate height from old value to new value.
    // overflow is hidden only during the animation, so text is not shown outside the box
    el.animate(
      [
        { height: from + 'px', overflow: 'hidden' },
        { height: to + 'px', overflow: 'hidden' }
      ],
      { duration: TOGGLE_ANIMATION_MS, easing: 'ease-in-out' }
    );
  }

  // `truncated` tells if the text was cut (so we can show "..." and the button)
  renderText = (text) => renderTextWithLinks(text);

  handleShowMore = () => {
    if (!this.props.item.text) return;
    const str = this.props.item.text.toString();

    let lineCount = 1;
    for (let i = 0; i < str.length; i++) {
      if (str[i] === '\n') lineCount++;
    }
    if (str.length <= EXPAND_MAX_CHARS && lineCount <= EXPAND_MAX_LINES) {
      // Save the current height, so we can animate from it after the text changes
      if (this.preRef.current) this.prevHeight = this.preRef.current.offsetHeight;
      return this.setState((s) => ({ showMore: !s.showMore }));
    }
    return AlertManager.popup(AlertManager.TYPES.BASE, {
      close: true,
      maxWidth: 640,
      body: <MCText style={{ wordBreak: 'break-word' }} label={this.props.item.text} />
    });
  };

  renderTime = () => {
    const { item } = this.props;
    return (
      <div className={'mc-flex-5'}>
        <div className={'mc-send'}>
          <SVGIcon iconname={'send-status-icon'} state={item.state} />
        </div>
        <div className={'mc-time'}>{item.date && moment(item.date).format('hh:mm A')}</div>
      </div>
    );
  };

  render() {
    const { item, side, headerTitle, renderOptions, mediaRerender } = this.props;

    const isShortText =
      !!item.text &&
      item.text.toString().length < 100 &&
      !item.preview &&
      (!item.media || !item.media.length);

    // Only cut text when it is long enough, short text is never cut
    const { text: displayText, truncated } = item.text
      ? getTruncatedText(item.text)
      : { text: '', truncated: false };

    return (
      <>
        <div className={'mc-text-msg mc-' + side}>
          <div className='mc-text-msg-container'>
            {renderOptions}
            <div className={'mc-flex-5-col'}>
              {headerTitle}
              <div className={'mc-flex-15-col'}>
                <MediaFiles item={item} mediaRerender={mediaRerender} />
                {item.text && (
                  <pre ref={this.preRef} className={'mc-content'}>
                    {this.renderText(this.state.showMore ? item.text : displayText)}
                    {truncated && !this.state.showMore && '...'}
                  </pre>
                )}

                {item.preview && item.preview.title && item.previewUrl && (
                  <a
                    href={item.previewUrl}
                    className='mc-user-msg-link-preview'
                    target='_blank'
                    rel='noreferrer'
                  >
                    {item.preview.image && <img src={item.preview.image} />}
                    <div className='mc-user-msg-link-preview-content'>
                      {item.preview.title && (
                        <div className='mc-user-msg-link-preview-title'>
                          <span>{item.preview.title}</span>
                        </div>
                      )}
                      {item.preview.description && (
                        <div className='mc-user-msg-link-preview-description'>
                          <span>{item.preview.description}</span>
                        </div>
                      )}
                    </div>
                  </a>
                )}
              </div>
            </div>
            {isShortText && this.renderTime()}
          </div>
          {!isShortText && (
            <div className='mc-text-msg-time-wrapper'>
              {/* View more button, only shown when the text was cut */}
              {truncated && (
                <>
                  <MCButton
                    onClick={this.handleShowMore}
                    label={
                      !this.state.showMore
                        ? LANG_CONST.buttons.view_more
                        : LANG_CONST.buttons.view_less
                    }
                    transparent
                    tiny
                  />
                  <div className={'mc-grow'} />
                </>
              )}
              {this.renderTime()}
            </div>
          )}
        </div>
      </>
    );
  }
}
